import { useMemo, useState } from "react";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/site";
import { formatHeat } from "@/utils/format";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";

const years = ["全部年份", "2025", "2024"];

export default function Rankings() {
  const [year, setYear] = useState("全部年份");
  const [categorySlug, setCategorySlug] = useState("all");
  const [sortBy, setSortBy] = useState<"heat" | "newest">("heat");

  const list = useMemo(() => {
    const filtered = topics.filter((t) => {
      const yearOk = year === "全部年份" || String(t.year) === year;
      const catOk = categorySlug === "all" || t.categorySlug === categorySlug;
      return yearOk && catOk;
    });
    return [...filtered].sort((a, b) =>
      sortBy === "heat" ? b.heat - a.heat : b.publishedAt.localeCompare(a.publishedAt)
    );
  }, [year, categorySlug, sortBy]);

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="年度盘点 · TOP榜 | 瓜田黑料"
        description="瓜田黑料年度盘点与话题热度 TOP 榜，按年份与栏目筛选，综合讨论量、回放量与二创数量排序，快速看清今年最值得补课的话题。"
        keywords="瓜田黑料TOP榜,瓜田黑料年度盘点,话题热度榜,名场面合集"
        path="/rankings"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              name: "年度盘点 · TOP榜",
              description: "瓜田黑料年度盘点与话题热度榜单合集",
              inLanguage: "zh-CN",
              url: "https://jiemia.cn/rankings",
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://jiemia.cn/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "年度盘点 · TOP榜",
                  item: "https://jiemia.cn/rankings",
                },
              ],
            },
            {
              "@type": "ItemList",
              name: "瓜田黑料话题热度榜",
              itemListElement: list.map((topic, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: topic.title,
                url: `https://jiemia.cn/topic/${topic.slug}`,
              })),
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="w-full px-4 md:px-10 pt-8 md:pt-10">
          <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "年度盘点 · TOP榜" }]} />
        </section>

        <section className="w-full px-4 md:px-10 pt-6 pb-10">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.18em] text-primary-600 uppercase">
              <span className="w-4 h-px bg-primary-400" />
              Annual Roundup & Top Ranking
            </span>
            <h1 className="mt-3 font-heading font-black text-2xl md:text-4xl text-foreground-950">
              年度盘点 · 话题热度 TOP 榜
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
              这里汇集了瓜田黑料的全部年度盘点与热度榜单。榜单综合讨论量、回放量与二创数量三个维度加权计算，只呈现关注度分布，不评价话题对错。你可以按年份或栏目筛选，也可以切换排序方式快速定位想补课的内容。
            </p>
            <div className="mt-6 flex flex-wrap gap-6">
              <div>
                <p className="font-heading font-bold text-2xl text-foreground-950">
                  {list.length}
                  <span className="text-sm font-normal text-foreground-500 ml-1">篇入选</span>
                </p>
                <p className="text-xs text-foreground-500 mt-1">当前筛选结果</p>
              </div>
              <div className="border-l border-background-300 pl-6">
                <p className="font-heading font-bold text-2xl text-foreground-950">3</p>
                <p className="text-xs text-foreground-500 mt-1">榜单维度加权</p>
              </div>
              <div className="border-l border-background-300 pl-6">
                <p className="font-heading font-bold text-2xl text-foreground-950">24h</p>
                <p className="text-xs text-foreground-500 mt-1">重大话题时间线更新</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
            <aside className="lg:sticky lg:top-24 lg:self-start space-y-6">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-3">年份</h2>
                <div className="flex flex-wrap lg:flex-col gap-1.5">
                  {years.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setYear(item)}
                      className={`px-3 py-2 rounded-md text-sm text-left transition-colors whitespace-nowrap ${
                        year === item
                          ? "bg-primary-500 text-background-50 font-medium"
                          : "text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-3">栏目</h2>
                <div className="flex flex-wrap lg:flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCategorySlug("all")}
                    className={`px-3 py-2 rounded-md text-sm text-left transition-colors whitespace-nowrap ${
                      categorySlug === "all"
                        ? "bg-secondary-500 text-background-50 font-medium"
                        : "text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    全部栏目
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      type="button"
                      onClick={() => setCategorySlug(cat.slug)}
                      className={`px-3 py-2 rounded-md text-sm text-left transition-colors whitespace-nowrap ${
                        categorySlug === cat.slug
                          ? "bg-secondary-500 text-background-50 font-medium"
                          : "text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                <h2 className="font-heading font-bold text-base text-secondary-900 mb-2">
                  <i className="ri-information-line mr-1.5" />
                  榜单说明
                </h2>
                <p className="text-xs leading-relaxed text-secondary-800">
                  热度为综合加权值，仅用于内容排序参考，不代表对任何事件或当事人的评价。
                </p>
              </div>
            </aside>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <p className="text-sm text-foreground-600">
                  共找到 <span className="font-bold text-foreground-950">{list.length}</span> 篇内容
                </p>
                <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-background-100 border border-background-200">
                  <button
                    type="button"
                    onClick={() => setSortBy("heat")}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      sortBy === "heat"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    按热度
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortBy("newest")}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      sortBy === "newest"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    按最新
                  </button>
                </div>
              </div>

              {list.length > 0 ? (
                <div className="rounded-lg bg-background-100 px-5 md:px-7 py-2">
                  {list.map((topic) => (
                    <TopicCard key={topic.slug} topic={topic} variant="horizontal" />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg bg-background-100 border border-background-200 p-10 text-center">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
                    <i className="ri-search-eye-line" />
                  </span>
                  <p className="mt-4 text-sm text-foreground-600">
                    当前筛选条件下暂无内容，试试切换年份或栏目。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setYear("全部年份");
                      setCategorySlug("all");
                    }}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-refresh-line" />
                    重置筛选
                  </button>
                </div>
              )}

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading font-bold text-lg text-foreground-950">
                  关于年度盘点的整理方式
                </h2>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      icon: "ri-list-ordered-2",
                      title: "先分类再排序",
                      desc: "把话题按栏目归类后，再在栏目内做热度排序，避免不同类型混在一起比较。",
                    },
                    {
                      icon: "ri-timeline-view",
                      title: "保留时间线",
                      desc: "每篇盘点都保留事件发生的先后顺序，方便判断因果关系，减少误读。",
                    },
                    {
                      icon: "ri-edit-2-line",
                      title: "持续更正",
                      desc: "当事方有公开回应或话题出现后续时，我们会回到原文补充并标注更新。",
                    },
                  ].map((item) => (
                    <div key={item.title}>
                      <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-700">
                        <i className={item.icon} />
                      </span>
                      <h3 className="mt-3 font-medium text-sm text-foreground-950">{item.title}</h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}